import { SparklesIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";

import { classNames } from "../../../../common/lib/classNames";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { useContact } from "../../../hooks/useContact";
import { useFetchContactAISummaryQuery } from "../../../services/api/contactsApi/contact";

interface IContactAISummaryProps {
  className?: string;
}

/** The AI summary sentences for the route contact, empty while loading or when generation failed */
export function useContactAISummaryTexts() {
  const { data: contact, isSuccess } = useContact();
  const { data: contactAISummary, isLoading } = useFetchContactAISummaryQuery({
    organisationId: contact?.organisationId,
    contactId: contact?.id,
  }, {
    // Only load once contact has loaded
    skip: !isSuccess || !contact,
    refetchOnMountOrArgChange: true,
  });

  if (isLoading || !contactAISummary || contactAISummary.failed) {
    return [];
  }

  return contactAISummary.summaryTexts?.filter(text => text?.trim()) ?? [];
}

export function ContactAISummary({
  className,
}: IContactAISummaryProps) {
  const summaryTexts = useContactAISummaryTexts();

  if (summaryTexts.length === 0) {
    return null;
  }

  return (
    <div
      className={ classNames(
        `mt-4 rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3`,
        className,
      ) }
    >
      <div className={ `flex items-start gap-3` }>
        <Tooltip title={ `AI-generated summary of this contact based on their activity and history` }>
          <div className={ `shrink-0 rounded-md bg-violet-50 p-2` }>
            <SparklesIcon className={ `h-5 w-5 text-violet-600` } />
          </div>
        </Tooltip>
        <div className={ `min-w-0 flex-1 text-left` }>
          <span className={ `text-[11px] uppercase tracking-wide text-gray-500` }>
            { `AI Summary` }
          </span>
          <ul className={ `mt-2 list-disc pl-5 space-y-1` }>
            { summaryTexts.map((text, i) => (
              <li key={ i }>
                <Paragraph
                  variant={ `note` }
                  className={ `text-left text-gray-700` }
                >
                  { text.trim() }
                </Paragraph>
              </li>
            )) }
          </ul>
        </div>
      </div>
    </div>
  );
}
